import { Component, Element, h, Prop, State, Event, EventEmitter } from "@stencil/core";
import { AppStoreType, AppStateType, getRandomId } from "../../utils/utils";
import { sortUpdated } from "../../store/sort";
import { ProgressRating } from "../../utils/funcComponents";
import { Unsubscribe } from "../../utils/types";

@Component({
  tag: "covet-pics-gallery-header",
  styleUrl: "covet-pics-gallery-header.scss"
})

export class CovetPicsGalleryHeader {
  @Element() el: HTMLCovetPicsGalleryHeaderElement;

  /**
   * rating
   */
  @Prop() rating: number = 0;
  
  /**
   * product-title in header
   */
  @Prop() productTitle: string = "";

  /**
   * array of breakdown for header
   */
  @Prop() breakdown: Array<any> = [];

  /**
   * app store 
  */
  @Prop() appStore: AppStoreType;

  // App state;
  private appState: AppStateType;
  private unsubscribe: Unsubscribe;

  @State() showBreakdownDropdown: boolean = false;
  @State() showSortDropdown: boolean = false;
  @State() isDesktopScreen: boolean;

  // unique breakdown id
  private breakdownId: string;
  private sortSelectId: string;
  private headerType: string; // short var name (`header1`, `header2`,`header3`, `header4`)

  /**
   *  Emitted sortUpdated
   */
  @Event() sortUpdated: EventEmitter;

  componentWillLoad() {
    this.initStore();
    this.breakdownId = `dropdown-menu-${getRandomId(16)}`;
    this.sortSelectId = `dropdown-sort-${getRandomId(16)}`;
  }

  disconnectedCallback() {
    this.unsubscribe();
  }
  
  private initStore() {
    this.appState = this.appStore.getState();
    this.isDesktopScreen = this.appState.size.isDesktopScreen;
    this.headerType = this.appState.settings.header_type;
    this.unsubscribe = this.appStore.subscribe(() => {
      this.appState = this.appStore.getState();
      this.headerType = this.appState.settings.header_type;
      this.isDesktopScreen = this.appState.size.isDesktopScreen;
    });
  }

  private onClickAddReviewHendler = (event) => {
    event.preventDefault();
    event.stopPropagation();
    let uploadGalleryItem = this.el.parentElement.querySelector(".upload-widget") as HTMLElement;
    if (uploadGalleryItem !== null) return uploadGalleryItem.click();
  
    const uploadWdiget = document.querySelector("covet-pics-upload") as HTMLCovetPicsUploadElement;
    if (uploadWdiget !== null) return uploadWdiget.openUpload();

    return false;
  }

  private onClickRatingHendler = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.showBreakdownDropdown = !this.showBreakdownDropdown;
  }

  private mouseEnterRatingHandler = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.showBreakdownDropdown = true;
  }

  private mouseLeaveRatingHandler = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.showBreakdownDropdown = false;
  }

  private getBreakdown(): Array <any> {
    const newBreakdown = [];
    const starsScore = ["one", "two", "three", "four", "five"];
    for (let i = 5; i > 0; i--) {
      const itemIndex = this.breakdown.findIndex(el => el.rating === i);
      if (itemIndex !== -1) {
        const item = {...this.breakdown[itemIndex], ...{starLabel: starsScore[i - 1]}};
        newBreakdown.push(item);
      }
      else {
        newBreakdown.push({
          rating: i,
          count: 0,
          rating_percentage: 0,
          id: null,
          starLabel: starsScore[i - 1]
        });
      }
    }
    return newBreakdown ;
  }

  private onSelectUpdate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.showSortDropdown = !this.showSortDropdown;
    if (event.target.getAttribute("aria-selected") === undefined) return false;
    this.appStore.dispatch(sortUpdated(event.target.getAttribute("aria-selected")));
    this.sortUpdated.emit(event.target.getAttribute("aria-selected")); //TODO remove this (call this in component use sibscribe)
    // a11y focus
    const sortBtn = this.el.querySelector(".sort-btn") as HTMLButtonElement;
    sortBtn.focus();
  }

  private renderBreakdown() {
    return (
      <div id={this.breakdownId} class={`rating-breakdown ${this.headerType} ${this.showBreakdownDropdown ? "show-breakdown" : ""}`} role="listbox" aria-labelledby="dropdown-label" aria-activedescendant="selected-option" tabindex="-1">
        <h3 class="rating-breakdown-title">Review ratings breakdown</h3>
        {this.getBreakdown().map(el => 
          <div class="rating-breakdown-score" role="option">
            <covet-pics-star-rating
              rating={el.rating}
              showLabel={false}
              starSize={10} 
              spaceStars={8} 
              emptyStarColor={"transpotent"}
              starBorderColor={"#fec500"}
              starBorderWidth={10}>   
            </covet-pics-star-rating>
            <p class="rating-breakdown-text">
              {el.starLabel} {el.starLabel !== "one" ? "stars" : "star"}
            </p>
            <span class={`score ${this.headerType}`}>{el.count}</span>
          </div>)}
      </div > 
    );
  }

  private renderSort() {
    const mobileSort = !this.isDesktopScreen && (this.headerType === "header1" || this.headerType === "header4");
    const iconActiveClass = this.showSortDropdown ? "sort-arrow-down" : "";
    const mobileSortSelected = mobileSort ? "" : this.appState.sort.options.find(el => el.value === this.appState.sort.selected)?.name;
    return (
      <div class={`sorting ${this.headerType}`}>
        <span class={`sort-title ${this.headerType}`}>Sort {mobileSort ? "" : "by"} :</span>
        <button class={`sort-btn ${this.headerType}`} onClick={() => this.showSortDropdown = !this.showSortDropdown} 
          aria-haspopup="listbox" aria-expanded="false" aria-labelledby="dropdown-label dropdown-button" aria-controls={this.sortSelectId}>
          {/* TODO move class for text from sort-btn to sort-btn-text */}
          <span class="sort-btn-text"> 
            {mobileSortSelected}
          </span>
          {this.headerType === "header2" &&
            <svg class={`sort-arrow ${this.headerType} ${iconActiveClass}`} viewBox="0 0 8 6" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.5791 5.50671C4.37892 5.75887 3.99608 5.75887 3.79589 5.50671L0.663473 1.56088C0.403343 1.2332 0.636699 0.75 1.05508 0.75L7.31992 0.75C7.7383 0.75 7.97166 1.2332 7.71153 1.56088L4.5791 5.50671Z" fill="#1F273D"/>
            </svg>}
          {(this.headerType === "header1" || this.headerType === "header3") &&
            <svg class={`sort-arrow ${this.headerType} ${iconActiveClass}`} viewBox="0 0 9 6" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.8916 5.50671C4.69142 5.75887 4.30858 5.75887 4.10839 5.50671L0.380581 0.810878C0.120451 0.483199 0.353806 0 0.772186 0L8.22781 0C8.64619 0 8.87955 0.483199 8.61942 0.810878L4.8916 5.50671Z" fill="#919EAB"/>
            </svg>}
          {this.headerType === "header4" &&
            <svg class={`sort-arrow ${this.headerType} ${iconActiveClass}`} viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L5 5L9 1" stroke="#007ACE" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          }
        </button>
        <ul id={this.sortSelectId} class={`sort-select ${this.headerType} ${this.showSortDropdown ? "show-sort" : ""}`} role="listbox" 
          aria-hidden={`${!this.showSortDropdown}`} aria-labelledby="dropdown-label" aria-activedescendant="selected-option" tabindex="-1">
          {this.appState.sort.options.map(el => 
            <li class={`sort__dropdown-item ${el.value === this.appState.sort.selected ? "active" : ""}`} aria-selected={el.value} onClick={this.onSelectUpdate} role="option">
              <svg class="sort-check" viewBox="0 0 12 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10.7273 1.5L4.72727 7.5L2 4.77273" stroke="#919EAB" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              {el.name}
            </li>)}
        </ul>
      </div>
    );
  }

  private renderHeaderOne() {
    const mobileRating = !this.isDesktopScreen && (this.headerType === "header1" || this.headerType === "header4");
    return (
      <div class={`header ${this.headerType}`}>
        <div class={`rated-wrap ${this.headerType}`}>
          <button class="rating-btn" onClick={this.onClickRatingHendler} onMouseEnter={this.mouseEnterRatingHandler} onMouseLeave={this.mouseLeaveRatingHandler}
            aria-haspopup="listbox" aria-expanded="false" aria-labelledby="dropdown-label dropdown-button" aria-controls={this.breakdownId}>
            <covet-pics-star-rating
              rating={this.rating}
              showLabel={false}
              starSize={15} 
              spaceStars={11} 
              emptyStarColor={"transpotent"}
              starBorderColor={"#fec500"}
              starBorderWidth={10}>   
            </covet-pics-star-rating>
            {this.appState.settings.header_show_average_rating && <p class="rating-text"><span class="rated">Rated {this.rating}/5 </span>{mobileRating ? `(${this.appState.settings.gallery_photos_count})` : `based on ${this.appState.settings.gallery_photos_count} customer reviews.`}</p>}
          </button>
          {this.appState.settings.header_show_average_rating && this.renderBreakdown()}
        </div>
        {this.appState.settings.header_show_sort&& this.renderSort()}
      </div>
    );
  }

  private renderHeaderTwo() {
    return (
      <div class={`header ${this.headerType}`}>
        {this.appState.settings.header_show_average_rating &&
          <div class={`rated-wrap ${this.headerType} ${this.appState.settings.header_show_breakdown ? "show-separation" : ""}`}>
            <div class={`rating-wrap ${this.headerType}`}>
              {this.isDesktopScreen && <h3 class="rating-title">Customers rated this:</h3>}
              <p class={`score ${this.headerType}`}>
                <span class={`score-number ${this.headerType}`}>{this.rating}</span> out of 5
                <span class={`score-total ${this.headerType}`}> from {this.appState.settings.gallery_photos_count} ratings</span>
              </p>
            </div>
            <ul class={`rating-breakdown ${this.headerType}`}  aria-label="gallery stars review rating">
              {this.getBreakdown().map(el =>
                <li class="rating-row"><ProgressRating rating={el.rating} percentage={el.rating_percentage} count={el.count}></ProgressRating></li>
              )}
            </ul>
          </div>}
        <div class={`sort-wrap ${this.headerType}`}>
          {this.appState.settings.header_show_sort&& this.renderSort()}
        </div>
      </div>
        
    );
  }

  private renderHeaderThree() {
    return (
      <div class={`header ${this.headerType}`}>
        <div class={`rated-wrap ${this.headerType}`}>
          <div class="rating-wrap">
            <covet-pics-star-rating
              rating={4}
              showLabel={false}
              starSize={18} 
              spaceStars={11} 
              emptyStarColor={"transpotent"}
              starBorderColor={"#fec500"}
              starBorderWidth={10}>   
            </covet-pics-star-rating>
            {this.appState.settings.header_show_average_rating && <p class={`score ${this.headerType}`}>
              <span class={`score-number ${this.headerType}`}>{this.rating}</span> out of 5 stars</p>}
            <p class={`score-total ${this.headerType}`}>{this.appState.settings.gallery_photos_count} reviews</p>
          </div>
          <div class="review-wrap">
            <button class="review-btn" onClick={this.onClickAddReviewHendler}>
              <svg class="icon-pen" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5.94629 10.4102H10.8928" stroke="white" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M8.4197 1.34148C8.63834 1.12283 8.93489 1 9.24411 1C9.39721 1 9.54882 1.03016 9.69027 1.08875C9.83173 1.14734 9.96025 1.23322 10.0685 1.34148C10.1768 1.44975 10.2627 1.57827 10.3213 1.71972C10.3798 1.86118 10.41 2.01279 10.41 2.16589C10.41 2.319 10.3798 2.47061 10.3213 2.61206C10.2627 2.75351 10.1768 2.88204 10.0685 2.9903L3.19843 9.86039L1 10.41L1.54961 8.21157L8.4197 1.34148Z" stroke="white" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              {this.isDesktopScreen && "Write a review"} </button>
          </div>
        </div>
        <div class={`sort-wrap ${this.headerType}`}>
          {this.appState.settings.header_show_sort&& this.renderSort()}
        </div>
        
      </div>
    );
  }

  private renderHeaderFour() {
    return (
      <div class={`header ${this.headerType}`}>
        <div class={`rated-wrap ${this.headerType}`}>
          <p class="top-title">
            <svg class="star-title" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.5 0.75L5.65875 3.0975L8.25 3.47625L6.375 5.3025L6.8175 7.8825L4.5 6.66375L2.1825 7.8825L2.625 5.3025L0.75 3.47625L3.34125 3.0975L4.5 0.75Z" fill="#929EAB" stroke="#929EAB" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            costomer reviews for</p>
          <h3 class={`rating-title ${this.headerType}`}>{this.productTitle}</h3>
          <div class={`score ${this.headerType}`}> 
            <covet-pics-star-rating
              rating={this.rating}
              showLabel={false}
              starSize={12} 
              spaceStars={11} 
              emptyStarColor={"transpotent"}
              starBorderColor={"#fec500"}
              starBorderWidth={10}>   
            </covet-pics-star-rating>
            {this.appState.settings.header_show_average_rating &&
              <span><span class={`score-number ${this.headerType}`}>{this.rating}</span> {this.isDesktopScreen && "average rating"}</span>
            }
            {this.appState.settings.header_show_average_rating && <span class="score-slash"></span>}
            <span><span class={`score-total ${this.headerType} ${!this.appState.settings.header_show_average_rating ? "space-left": 0}`}>{this.appState.settings.gallery_photos_count}</span> reviews</span>
          </div>
        </div>
        <div class={`sort-wrap ${this.headerType}`}>
          {this.appState.settings.header_show_sort&& this.renderSort()}
        </div>
      </div>
    );
  }
   
  render() {
    if (this.headerType === "header1")
      return this.renderHeaderOne();
    if (this.headerType === "header2")
      return this.renderHeaderTwo();
    if (this.headerType === "header3")
      return this.renderHeaderThree();
    if (this.headerType === "header4")
      return this.renderHeaderFour();
    
  }  
}
